import { Button, MenuItem } from "@chakra-ui/react";
import { Tab, TabPanel, TabPanels, Tabs } from "@chakra-ui/tabs";
import { useQueryClient } from "@tanstack/react-query";
import { InternalIntegrationTestingComparisonModal } from "features/internal/integration-testing/tabs/InternalIntegrationTestingCompareModal";
import { InternalIntegrationTestingTicketModal } from "features/internal/integration-testing/tabs/InternalIntegrationTestingTicketModal";
import { InternalIntegrationTestingValidationChecksModal } from "features/internal/integration-testing/tabs/InternalIntegrationTestingValidationChecksModal";
import {
  getInternalIntegrationTestingIssuesTableQueryKey,
  useIntegrationTestingIssueDelete,
  useInternalIntegrationTestingIssuesTableSuspense,
} from "gen/endpoints/integration-testing-issues/integration-testing-issues";
import {
  getInternalIntegrationTestingQueueTableQueryKey,
  useInternalIntegrationTestingQueueItemDelete,
  useInternalIntegrationTestingQueueTableSuspense,
} from "gen/endpoints/integration-testing-queue/integration-testing-queue";
import { useInternalIntegrationTestingReportsTableSuspense } from "gen/endpoints/integration-testing-reports/integration-testing-reports";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { PageTabBody } from "shared/components/layout/PageTabBody";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTabs } from "shared/components/table/box/TableBoxTabs";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import {
  GetLinkInfoForCellFn,
  RenderRowActionsFn,
} from "shared/components/table/data/DataTableTypes";
import { rootRoute } from "shared/navigation/Routes";
import { useQueryParam } from "use-query-params";
import { useBoolean } from "usehooks-ts";

const TAB_IDS = ["queued", "history"] as const;
type TabId = (typeof TAB_IDS)[number];

export const InternalIntegrationTestingActivityTab: React.FC = () => {
  const { t } = useTranslation(["internal", "common"]);
  const queryClient = useQueryClient();

  const {
    value: isTicketModalOpen,
    setTrue: openTicketModal,
    setFalse: closeTicketModal,
  } = useBoolean();

  const {
    value: isValidationChecksModalOpen,
    setTrue: openValidationChecksModal,
    setFalse: closeValidationChecksModal,
  } = useBoolean();

  const {
    value: isComparisonModalOpen,
    setTrue: openComparisonModal,
    setFalse: closeComparisonModal,
  } = useBoolean();

  const { data: historyTableData } =
    useInternalIntegrationTestingReportsTableSuspense();

  const { data: queueTableData } =
    useInternalIntegrationTestingQueueTableSuspense();

  const { data: issuesTableData } =
    useInternalIntegrationTestingIssuesTableSuspense();

  const { mutateAsync: deleteTestingIssue } = useIntegrationTestingIssueDelete({
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: getInternalIntegrationTestingIssuesTableQueryKey(),
        });
      },
    },
  });

  const { mutateAsync: deleteTestingQueueItem } =
    useInternalIntegrationTestingQueueItemDelete({
      mutation: {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: getInternalIntegrationTestingQueueTableQueryKey(),
          });
        },
      },
    });

  const [selectedTab, setSelectedTab] = useQueryParam<TabId | null>("");
  const defaultTab: TabId = queueTableData.rows.length ? "queued" : "history";
  const tabIndex = TAB_IDS.indexOf(selectedTab || defaultTab);

  const onChangeTab = useCallback(
    (index: number) => setSelectedTab(TAB_IDS[index]),
    [setSelectedTab],
  );

  const getIssueTableLinkInfoForCell = useCallback<GetLinkInfoForCellFn>(
    (props) => {
      if (
        props.columnContext.column.id == "ticket_number" &&
        props.type == "data" &&
        props.cell.value?.type == "text"
      ) {
        const ticketNumber = props.cell.value.text;
        return {
          type: "generic",
          to: `https://maybern.atlassian.net/browse/${ticketNumber}`,
        };
      }
      return undefined;
    },
    [],
  );

  const getReportsTableLinkInfoForCell = useCallback<GetLinkInfoForCellFn>(
    (props) => {
      if (
        props.columnContext.column.id == "git_commit_hash" &&
        props.type == "data" &&
        props.cell.value?.type == "text"
      ) {
        const gitCommitHash = props.cell.value.text;
        return {
          type: "generic",
          to: `https://github.com/Maybern/maybern/commit/${gitCommitHash}`,
        };
      } else if (
        props.columnContext.column.id == "created_at" &&
        props.type == "data" &&
        props.row.data?.metadata?.type === "id_row"
      ) {
        return {
          type: "generic",
          to: rootRoute({}).internal({}).integrationTesting({}).report({
            integrationTestingReportId: props.row.data.metadata.id,
          }).$,
        };
      }
      return undefined;
    },
    [],
  );

  const issueTableRenderRowActions = useCallback<RenderRowActionsFn>(
    ({ row }) => {
      const metadata = row.data?.metadata;
      if (metadata?.type === "id_row") {
        return {
          type: "menu",
          actions: (
            <MenuItem
              className="destructive"
              onClick={() =>
                deleteTestingIssue({ testingIssueId: metadata.id })
              }
            >
              {t("common:delete")}
            </MenuItem>
          ),
        };
      }
      return undefined;
    },
    [deleteTestingIssue, t],
  );

  const queueTableRenderRowActions = useCallback<RenderRowActionsFn>(
    ({ row }) => {
      const metadata = row.data?.metadata;
      if (metadata?.type === "id_row") {
        return {
          type: "menu",
          actions: (
            <MenuItem
              className="destructive"
              onClick={() =>
                deleteTestingQueueItem({ testingQueueId: metadata.id })
              }
            >
              {t("common:delete")}
            </MenuItem>
          ),
        };
      }
      return undefined;
    },
    [deleteTestingQueueItem, t],
  );

  return (
    <PageTabBody>
      {isTicketModalOpen && (
        <InternalIntegrationTestingTicketModal
          onCloseComplete={closeTicketModal}
        />
      )}
      {isComparisonModalOpen && (
        <InternalIntegrationTestingComparisonModal
          onCloseComplete={closeComparisonModal}
        />
      )}
      {isValidationChecksModalOpen && (
        <InternalIntegrationTestingValidationChecksModal
          onCloseComplete={closeValidationChecksModal}
        />
      )}
      <TableBox>
        <TableBoxHeader>
          <TableBoxTitle>
            {t("internal:integrationTesting.issuesTable.title")}
          </TableBoxTitle>
          <DataTableBoxActions>
            <Button colorScheme="tertiary" size="xs" onClick={openTicketModal}>
              {t("internal:modal.createTicket.title")}
            </Button>
          </DataTableBoxActions>
        </TableBoxHeader>
        <TableBoxBody>
          <DataTable
            data={issuesTableData}
            renderRowActions={issueTableRenderRowActions}
            getLinkInfoForCell={getIssueTableLinkInfoForCell}
            emptyStateProps={{
              title: t(
                "internal:integrationTesting.issuesTable.emptyState.title",
              ),
              description: t(
                "internal:integrationTesting.issuesTable.emptyState.description",
              ),
            }}
          />
        </TableBoxBody>
      </TableBox>
      <TableBox>
        <TableBoxHeader>
          <TableBoxTabs index={tabIndex} onChange={onChangeTab}>
            <Tab>
              {t("internal:integrationTesting.queuedIntegrationTestJobs.title")}
            </Tab>
            <Tab>{t("common:history")}</Tab>
          </TableBoxTabs>
          <DataTableBoxActions>
            <Button
              colorScheme="tertiary"
              size="xs"
              onClick={openValidationChecksModal}
            >
              {t("internal:modal.runValidationChecks.title")}
            </Button>
            <Button
              colorScheme="tertiary"
              size="xs"
              onClick={openComparisonModal}
            >
              {t("internal:modal.comparison.title")}
            </Button>
          </DataTableBoxActions>
        </TableBoxHeader>
        <TableBoxBody>
          <Tabs variant="table" index={tabIndex} isLazy={true}>
            <TabPanels>
              <TabPanel>
                <DataTable
                  renderRowActions={queueTableRenderRowActions}
                  data={queueTableData}
                  emptyStateProps={{
                    title: t(
                      "internal:integrationTesting.queuedIntegrationTestJobs.emptyState.title",
                    ),
                    description: t(
                      "internal:integrationTesting.queuedIntegrationTestJobs.emptyState.description",
                    ),
                  }}
                />
              </TabPanel>
              <TabPanel>
                <DataTable
                  data={historyTableData}
                  getLinkInfoForCell={getReportsTableLinkInfoForCell}
                />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </TableBoxBody>
      </TableBox>
    </PageTabBody>
  );
};
